import React, {Fragment, useEffect, useState} from "react";
import {
    Box,
    Button,
    ChakraProvider,
    FormControl,
    FormLabel,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    useToast,
} from "@chakra-ui/react";
import {MultiSelect} from 'chakra-multiselect'
import Papa from 'papaparse';
import * as XLSX from "xlsx";

import MessageApi from '../../../../api/message'
import PlaceholderInput from "./PlaceholderInput";
import Switcher from "./Switcher";
import FileDropzone from "./FileDropzone";
import PhoneNumberInputs from "./PhoneNumberInputs";
import CompanyDropdownSelection from "../../../../functions/CompanyDropdownSelection";
import { useWhatsAppTemplates } from '../context/WhatsAppTemplatesContext';

const recipientParamsFromRegistered = ['firstName', 'lastName', 'fullName', 'companyName', 'phoneNumber', 'dateOfBirth', 'jobTitle', 'availableToTransfer'];

const NewMessageModal = ({isOpen, onClose}) => {
    const [messageTitle, setMessageTitle] = useState("");
    const [messageBody, setMessageBody] = useState("");
    const [chosenCompanies, setChosenCompanies] = useState([])
    const [selectedFilters, setSelectedFilters] = useState([])
    const [messageType, setMessageType] = useState("push");
    const [selectedWhatsappTemplateId, setSelectedWhatsappTemplateId] = useState(0);
    const [recipientParamsFromFile, setRecipientParamsFromFile] = useState([]);
    const [parameterIdToParameterName, setParameterIdToParameterName] = useState({})
    const [customInputVisible, setCustomInputVisible] = useState({});
    const [customInputValue, setCustomInputValue] = useState({});
    const [recipientsFromFile, setRecipientsFromFile] = useState([]);
    const [isForRegistered, setIsForRegistered] = useState(false);
    const [nonRegisterRecipientType, setNonRegisterRecipientType] = useState("");
    const [recipientsByPhoneNumbers, setRecipientsByPhoneNumbers] = useState([]);
    const [whatsAppMediaUrl, setWhatsAppMediaUrl] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [templateSearch, setTemplateSearch] = useState("");
    const [showTemplateSuggestions, setShowTemplateSuggestions] = useState(false);

    const toast = useToast()

    const { whatsappTemplates, isWhatsappTemplatesLoading, getParametersFromText } = useWhatsAppTemplates();

    useEffect(() => {
        if (!isOpen) {
            setMessageTitle("");
            setMessageBody("");
            setChosenCompanies([])
            setSelectedFilters([])
            setParameterIdToParameterName({});
            setCustomInputVisible({});
            setCustomInputValue({});
            setRecipientParamsFromFile([]);
            setWhatsAppMediaUrl("");
            setTemplateSearch("");
            setShowTemplateSuggestions(false);
        }
    }, [isOpen]);

    const handleTitleChange = (event) => {
        setMessageTitle(event.target.value);
    }

    const handleBodyChange = (value) => {
        setMessageBody(value);
    }

    function getChosenCompanyIds(){
        return chosenCompanies.map((company) => company.id);
    }

    const handleSubmit = (event) => {
        event.preventDefault();

        let message = null;
        if (messageType === 'whatsapp') {
            let template = whatsappTemplates.find(t => t.id === selectedWhatsappTemplateId);
            let parameterNumbers = getParametersFromText(template.preview);
            let maxParameterNumber = Math.max(...parameterNumbers);
            let parameters = [];
            for (let i = 0; i <= maxParameterNumber - 1; i++) {
                let value = parameterNumbers.find(p => Number.parseInt(p) === i + 1);
                parameters[i] = (value && parameterIdToParameterName[value]) || null;
            }
            if (isForRegistered){
                const chosenCompaniesId = getChosenCompanyIds();
                message = {
                    params: {
                        templateName: template.name,
                        parameters: parameters,
                        companiesIds: chosenCompaniesId,
                    },
                    channelType: 2
                }
            } else {
                if(nonRegisterRecipientType === "excel") {
                    message = {
                        params: {
                            templateName: template.name,
                            parameters: parameters,
                            recipientsFromFile: recipientsFromFile,
                        },
                        channelType: 3
                    }
                }
                else {
                    message = {
                        params: {
                            templateName: template.name,
                            parameters: parameters,
                            recipientByPhoneNumber: recipientsByPhoneNumbers,
                        },
                        channelType: 4
                    }
                }
                // to message, the company to filter out
                if(selectedCompanyToFilterOut) {
                    message.companyIdToFilterOut = selectedCompanyToFilterOut;
                }
            }
            // add to message variable the media url
            if(whatsAppMediaUrl){
                message.mediaUrl = whatsAppMediaUrl;
            }
        } else if (messageType === 'push') {
            const chosenCompaniesId = getChosenCompanyIds();
            message = {
                params: {
                    title: messageTitle,
                    body: messageBody,
                    companiesId: chosenCompaniesId
                },
                channelType: 1
            }
        }
        if(message && message.params && selectedFilters && selectedFilters.length > 0){
            message.params.filters = selectedFilters;
        }

        setIsSending(true);
        MessageApi.sendMessage(message).then(res => {
            const data = res.data;

            // Check if we have detailed results from WhatsApp sending
            if (data && typeof data.successCount === 'number') {
                const { successCount, failCount, totalCount } = data;

                if (failCount === 0) {
                    // All messages sent successfully
                    toast({
                        title: `ההודעה נשלחה בהצלחה ל-${successCount} נמענים`,
                        position: 'top',
                        status: 'success',
                        isClosable: true,
                        duration: 5000,
                    });
                } else if (successCount === 0) {
                    // All messages failed
                    toast({
                        title: `שליחת ההודעה נכשלה לכל ${totalCount} הנמענים`,
                        position: 'top',
                        status: 'error',
                        isClosable: true,
                        duration: 8000,
                    });
                } else {
                    // Some succeeded, some failed
                    toast({
                        title: `ההודעה נשלחה ל-${successCount} נמענים, נכשלה ל-${failCount} נמענים`,
                        position: 'top',
                        status: 'warning',
                        isClosable: true,
                        duration: 8000,
                    });
                }
            } else {
                // Fallback for other message types (push notifications, etc.)
                toast({
                    title: `ההודעה נשלחה בהצלחה`,
                    position: 'top',
                    status: 'success',
                    isClosable: false,
                });
            }
            onClose();
        }).catch(e => {
            // Try to get the specific error message from the backend
            let errorMessage = 'שגיאה בשליחת ההודעה';
            if (e.response && e.response.data) {
                if (typeof e.response.data === 'string') {
                    errorMessage = e.response.data;
                } else if (e.response.data.message) {
                    errorMessage = e.response.data.message;
                }
            }
            toast({
                title: errorMessage,
                position: 'top',
                status: 'error',
                isClosable: true,
                duration: 8000,
            })
        }).finally(() => {
            setIsSending(false);
        })

    }

    useEffect(() => {
      if (
        isOpen &&
        whatsappTemplates.length > 0 &&
        whatsappTemplates[0]?.id &&
        !isWhatsappTemplatesLoading
      ) {
        setSelectedWhatsappTemplateId(whatsappTemplates[0].id);
        setTemplateSearch(whatsappTemplates[0].name);
      }
    }, [isWhatsappTemplatesLoading, whatsappTemplates, isOpen]);


    const toggleRegistered = () => {
        setIsForRegistered(!isForRegistered);
    }

    const loadRecipientsFromFile = (file) => {
        const fileType = file.name.split('.').pop();

        if (fileType === 'csv') {
            Papa.parse(file, {
                header: true,
                complete: (results) => {
                    setParameterIdToParameterName({});
                    setRecipientsFromFile(results.data);
                    setRecipientParamsFromFile(Object.keys(results.data[0] || {}));
                }
            });
        } else if (fileType === 'xlsx' || fileType === 'xls') {
            const reader = new FileReader();
            reader.onload = (evt) => {
                const data = new Uint8Array(evt.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const worksheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[worksheetName];
                const json = XLSX.utils.sheet_to_json(worksheet);
                setParameterIdToParameterName({});
                setRecipientsFromFile(json)
                const jsonForParams = XLSX.utils.sheet_to_json(worksheet, {header:1});
                setRecipientParamsFromFile(jsonForParams[0] || {});
            };
            reader.readAsArrayBuffer(file);
        } else {
            console.error('Unsupported file type');
        }
    }

    const filtersIdCash = {
        "משתמשים בעלי יתרת משיכה" : "1",
        "מגזר הערבי" : "2",
        "משתמשים שטרם משכו" : "3",
    };


    const _filterOptions = [
        ...Object.keys(filtersIdCash).map((key) => ({
            value: filtersIdCash[key],
            label: key,
            key: filtersIdCash[key]
        })),
    ];

    const handleInputChange = (event) => {
        setRecipientsByPhoneNumbers(event.target.value);
    };

    const [selectedCompanyToFilterOut, setSelectedCompanyToFilterOut] = React.useState("")

    const handleCompanySelection = (companies) =>{
        setSelectedCompanyToFilterOut(companies.length > 0 ? companies[0].id : null)
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>יצירת הודעה חדשה</ModalHeader>
                <ModalBody>
                    <FormControl>
                        <FormLabel>סוג ההודעה</FormLabel>
                        <Select pr='12px' defaultValue='push' onChange={e => setMessageType(e.target.value)}>
                            <option key='push' value='push'>push</option>
                            <option key='whatsapp' value='whatsapp'>whatsapp</option>
                        </Select>
                    </FormControl>

                    <br/>
                    {messageType === 'whatsapp' && <FormControl>
                        <ChakraProvider>
                            <Switcher isChecked={isForRegistered} handleToggle={toggleRegistered} option1={'registered'}
                                      option2={'non registered'}/>
                        </ChakraProvider>
                    </FormControl>}
                    <br/>

                    <br/>
                    {(messageType === 'push' || (messageType === 'whatsapp' && isForRegistered)) && (
                        <FormControl>
                            <FormLabel>בחירת נמענים</FormLabel>
                            <CompanyDropdownSelection
                                key={String(isOpen)}
                                showAll={true}
                                selectedCompany={setChosenCompanies}
                                style={{ width: '100%' }}
                            />
                        </FormControl>
                    )}

                    {messageType === 'whatsapp' && !isForRegistered &&
                        <>
                            <FormControl>
                                <FormLabel>נמענים</FormLabel>
                                <Select pr='12px' defaultValue='push' onChange={e => setNonRegisterRecipientType(e.target.value)}>
                                    <option key='excel file' value='excel'>Recipient from excel file</option>
                                    <option key='phone number' value='phone number'>Choose recipient by entering phone number</option>
                                </Select>
                            </FormControl>

                            <FormControl>
                                <FormLabel>להסיר רשומים של חברה</FormLabel>
                                <CompanyDropdownSelection selectedCompany={handleCompanySelection} showAll={true}/>
                            </FormControl>

                        </>
                    }

                    {messageType === 'whatsapp' && !isForRegistered && nonRegisterRecipientType === "phone number" &&
                        <PhoneNumberInputs recipientsByPhoneNumbers={recipientsByPhoneNumbers} setRecipientsByPhoneNumbers={setRecipientsByPhoneNumbers} handleInputChange={handleInputChange}/>
                    }

                    {messageType === 'whatsapp' && !isForRegistered && nonRegisterRecipientType === "excel"&&
                        <FormControl>
                        <FileDropzone onFileLoaded={loadRecipientsFromFile}/>
                    </FormControl>
                    }

                    {messageType === 'whatsapp' &&
                        <Fragment>
                            <FormControl>
                                <FormLabel>בחירת תבנית</FormLabel>
                                {isWhatsappTemplatesLoading ? <span>Loading templates...</span> :
                                    <Fragment>
                                        <Box position="relative">
                                            <Input
                                                value={templateSearch}
                                                onChange={e => {
                                                    setTemplateSearch(e.target.value);
                                                    setShowTemplateSuggestions(true);
                                                    if (!e.target.value) setSelectedWhatsappTemplateId(0);
                                                }}
                                                onFocus={() => setShowTemplateSuggestions(true)}
                                                onBlur={() => setTimeout(() => setShowTemplateSuggestions(false), 150)}
                                                placeholder="חיפוש תבנית..."
                                            />
                                            {showTemplateSuggestions && (
                                                <Box
                                                    position="absolute"
                                                    zIndex={10}
                                                    bg="white"
                                                    border="1px solid #e2e8f0"
                                                    borderRadius="md"
                                                    w="100%"
                                                    maxH="200px"
                                                    overflowY="auto"
                                                    boxShadow="md"
                                                >
                                                    {whatsappTemplates
                                                        .filter(t => t.name.toLowerCase().includes(templateSearch.toLowerCase()))
                                                        .map(t => (
                                                            <Box
                                                                key={t.id}
                                                                px={3}
                                                                py={2}
                                                                cursor="pointer"
                                                                _hover={{ bg: 'gray.100' }}
                                                                onMouseDown={() => {
                                                                    setSelectedWhatsappTemplateId(t.id);
                                                                    setTemplateSearch(t.name);
                                                                    setShowTemplateSuggestions(false);
                                                                    setParameterIdToParameterName({});
                                                                }}
                                                            >
                                                                {t.name}
                                                            </Box>
                                                        ))
                                                    }
                                                </Box>
                                            )}
                                        </Box>
                                        <div>
                                            {whatsappTemplates.find(t => t.id === selectedWhatsappTemplateId)?.preview}
                                        </div>

                                        <FormControl>
                                            <FormLabel>פרמטרים</FormLabel>
                                            {
                                                getParametersFromText(whatsappTemplates.find(t => t.id === selectedWhatsappTemplateId)?.preview)
                                                    .map(p => <span key={p}> {p}:
                            <Select defaultValue="0" pr="12px"
                                    onChange={e => {
                                        if (e.target.value === 'custom') {
                                            setCustomInputVisible(prevState => ({ ...prevState, [p]: true }));
                                        } else {
                                            setParameterIdToParameterName(prevState => ({ ...prevState,[p]:e.target.value}))
                                            setCustomInputVisible(prevState => ({ ...prevState, [p]: false }));
                                        }
                                    }}>
                                <option value='0' disabled hidden>בחר שדה</option>
                                { (isForRegistered ? recipientParamsFromRegistered : recipientParamsFromFile).map(value => <option
                                    key={p + '.' + value} value={value}>{value}</option>)}
                                <option value='custom'>Custom</option>
                            </Select>
                                                        {customInputVisible[p] &&
                                                            <Input
                                                                value={customInputValue[p] || ''}
                                                                onChange={e => {
                                                                    setCustomInputValue(prevState => ({ ...prevState, [p]: e.target.value }));
                                                                    setParameterIdToParameterName(prevState => ({ ...prevState,[p]:e.target.value}))}
                                                                }
                                                            />
                                                        }
                                            </span>)
                                            }


                                            <Input
                                                mt={'10px'}
                                                placeholder={"Media URL"}
                                                onChange={e => {
                                                    setWhatsAppMediaUrl(e.target.value);
                                                }}/>

                                        </FormControl>
                                    </Fragment>
                                }
                            </FormControl>
                        </Fragment>
                    }

                    {messageType === 'push' &&
                        <Fragment>
                            <FormControl id="message-title" isRequired>
                                <FormLabel>כותרת ההודעה</FormLabel>
                                <Input type="text" value={messageTitle} onChange={handleTitleChange}/>
                            </FormControl>
                            <FormControl id="message-body" isRequired>
                                <FormLabel>גוף ההודעה</FormLabel>
                                <PlaceholderInput onMessageBodyChange={handleBodyChange}/>
                            </FormControl>
                        </Fragment>
                    }
                    {(messageType === 'push' || isForRegistered) && <MultiSelect
                        options={_filterOptions}
                        value={selectedFilters}
                        label='מגבלות נוספות'
                        onChange={setSelectedFilters}
                    />}

                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" onClick={onClose}>בטל</Button>
                    <Button colorScheme="blue" onClick={handleSubmit} isLoading={isSending} isDisabled={isSending}>שלח הודעה</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default NewMessageModal;
